import { type ImportProblem, type ImportRunDetail, isPanelCounts } from "@procuremate/shared";
import { ArrowLeft, CircleAlert, TriangleAlert } from "lucide-react";
import { type RefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Card } from "./admin-layout";
import { useImportRun } from "./api";
import { RunStatusBadge, runSummary } from "./imports-page";

/** Problems listed before "Show all" (a run keeps at most 1,000; the counts cover every line). */
const PROBLEMS_SHOWN = 50;

/** Admin → Imports → one run (WP-3.3): what it did, what it refused, by file line. */
export function ImportRunPage() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const run = useImportRun(id);
  const heading = useRef<HTMLHeadingElement>(null);
  const loadedId = run.data?.id;

  // Move focus to the heading once the run loads (screen readers announce the new page).
  useEffect(() => {
    if (loadedId) heading.current?.focus();
  }, [loadedId]);

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/admin/imports"
        className="inline-flex items-center gap-1 self-start text-sm font-semibold text-primary hover:underline"
      >
        <ArrowLeft aria-hidden="true" className="h-4 w-4" />
        {t("admin.imports.back")}
      </Link>
      {run.isError ? (
        <p role="alert" className="rounded-sm bg-danger-bg px-3 py-2 text-sm text-danger">
          {t("admin.errors.notFound")}
        </p>
      ) : null}
      {run.data ? <Report run={run.data} headingRef={heading} /> : null}
    </div>
  );
}

function Report({ run, headingRef }: { run: ImportRunDetail; headingRef: RefObject<HTMLHeadingElement | null> }) {
  const { t, i18n } = useTranslation();
  const dateFormat = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" });
  const number = new Intl.NumberFormat(i18n.language);
  const counts = run.counts;
  const warnings = Object.entries(counts?.warnings ?? {});
  const errors = Object.entries(counts?.errors ?? {});

  return (
    <>
      <section className="rounded-lg bg-surface p-5 shadow-card sm:p-6" aria-labelledby="run-title">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <h1
            id="run-title"
            ref={headingRef}
            tabIndex={-1}
            className="text-2xl font-bold break-all text-primary outline-none"
          >
            {run.fileName}
          </h1>
          <RunStatusBadge status={run.status} />
        </div>
        <p className="text-sm text-fg" aria-live="polite">
          {runSummary(run, t, i18n.language)}
        </p>
        <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-[auto_1fr]">
          <dt className="text-fg-muted">{t("admin.imports.uploadedBy")}</dt>
          <dd className="text-fg">
            {t("admin.imports.byAt", {
              name: run.importedBy ?? "—",
              at: dateFormat.format(new Date(run.createdAt)),
            })}
          </dd>
          {run.finishedAt ? (
            <>
              <dt className="text-fg-muted">{t("admin.imports.finished")}</dt>
              <dd className="text-fg">{dateFormat.format(new Date(run.finishedAt))}</dd>
            </>
          ) : null}
          <dt className="text-fg-muted">{t("admin.imports.size")}</dt>
          <dd className="text-fg">
            {t("admin.imports.megabytes", { size: (run.sizeBytes / 1024 / 1024).toFixed(1) })}
          </dd>
        </dl>
        {run.encoding === "windows-1252" ? (
          <p className="mt-3 text-sm text-fg-muted">{t("admin.imports.windowsEncoding")}</p>
        ) : null}
        {run.ignoredColumns.length > 0 ? (
          <p className="mt-2 text-sm text-fg-muted">
            {t("admin.imports.ignoredColumns", { columns: run.ignoredColumns.join(", ") })}
          </p>
        ) : null}
        {run.unknownColumns.length > 0 ? (
          <p className="mt-2 text-sm text-fg-muted">
            {t("admin.imports.unknownColumns", { columns: run.unknownColumns.join(", ") })}
          </p>
        ) : null}
      </section>

      {counts ? (
        <Card title={t("admin.imports.whatChanged")}>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {isPanelCounts(counts) ? (
              <Figures
                title={t("admin.imports.panel.now")}
                rows={[
                  [t("admin.imports.panel.suppliers"), counts.panel.suppliers],
                  [t("admin.imports.panel.entries"), counts.panel.entries],
                  [t("admin.imports.panel.individuals"), counts.panel.individuals],
                  [t("admin.imports.panel.valueContracts"), counts.panel.valueContracts],
                ]}
                format={number.format}
              />
            ) : (
              <Figures
                title={t("admin.imports.requisitions")}
                rows={[
                  [t("admin.imports.added"), counts.requisitions.added],
                  [t("admin.imports.replaced"), counts.requisitions.replaced],
                  [t("admin.imports.unchanged"), counts.requisitions.unchanged],
                  [t("admin.imports.rejected"), counts.requisitions.rejected],
                ]}
                format={number.format}
              />
            )}
            <Figures
              title={t("admin.imports.lines")}
              rows={[
                [t("admin.imports.linesRead"), counts.linesRead],
                [t("admin.imports.linesStored"), counts.linesStored],
              ]}
              format={number.format}
            />
            <div>
              <Figures
                title={t("admin.imports.suppliers")}
                rows={[
                  [t("admin.imports.suppliersCreated"), counts.suppliers.created],
                  [t("admin.imports.newSpellings"), counts.suppliers.newSpellings],
                  [t("admin.imports.queued"), counts.suppliers.queued],
                ]}
                format={number.format}
              />
              {counts.suppliers.queued > 0 ? (
                <Link to="/admin/suppliers" className="mt-2 inline-block text-sm font-semibold text-primary underline">
                  {t("admin.imports.reviewSuppliers")}
                </Link>
              ) : null}
            </div>
          </div>
          {errors.length + warnings.length > 0 ? (
            <ul className="mt-6 flex flex-col gap-1 text-sm">
              {errors.map(([code, n]) => (
                <li key={code} className="flex items-start gap-2 text-fg">
                  <CircleAlert aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
                  <span>
                    <span className="sr-only">{t("admin.imports.severity.ERROR")}: </span>
                    {t(`admin.imports.problemCount.${code}`, { count: n ?? 0 })}
                  </span>
                </li>
              ))}
              {warnings.map(([code, n]) => (
                <li key={code} className="flex items-start gap-2 text-fg">
                  <TriangleAlert aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-warning-fg" />
                  <span>
                    <span className="sr-only">{t("admin.imports.severity.WARNING")}: </span>
                    {t(`admin.imports.problemCount.${code}`, { count: n ?? 0 })}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </Card>
      ) : null}

      {run.problems.length > 0 ? <Problems problems={run.problems} truncated={run.problemsTruncated} /> : null}
    </>
  );
}

function Figures({ title, rows, format }: { title: string; rows: [string, number][]; format: (n: number) => string }) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-fg-muted">{title}</h3>
      <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-fg">{label}</dt>
            <dd className="text-right font-semibold text-fg tabular-nums">{format(value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Problems({ problems, truncated }: { problems: ImportProblem[]; truncated: boolean }) {
  const { t } = useTranslation();
  const [all, setAll] = useState(false);
  const shown = all ? problems : problems.slice(0, PROBLEMS_SHOWN);
  return (
    <Card title={t("admin.imports.problemsTitle")}>
      <p className="mb-3 max-w-prose text-sm text-fg-muted">{t("admin.imports.problemsIntro")}</p>
      <table className="w-full text-left text-sm">
        <caption className="sr-only">{t("admin.imports.problemsTitle")}</caption>
        <thead className="border-b border-border text-fg-muted">
          <tr>
            <th scope="col" className="w-20 py-2 pr-4 font-semibold">
              {t("admin.imports.line")}
            </th>
            <th scope="col" className="py-2 font-semibold">
              {t("admin.imports.problem")}
            </th>
          </tr>
        </thead>
        <tbody>
          {shown.map((p) => (
            <tr key={`${p.line}-${p.code}`} className="border-b border-border last:border-0">
              <td className="py-2 pr-4 align-top tabular-nums">{p.line}</td>
              <td className="py-2 align-top">
                <span className="flex items-start gap-2">
                  {p.severity === "ERROR" ? (
                    <CircleAlert aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
                  ) : (
                    <TriangleAlert aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-warning-fg" />
                  )}
                  <span>
                    <span className="sr-only">{t(`admin.imports.severity.${p.severity}`)}: </span>
                    {t(`admin.imports.problems.${p.code}`)}
                  </span>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-fg-muted">
        {!all && problems.length > shown.length ? (
          <Button variant="secondary" onClick={() => setAll(true)}>
            {t("admin.imports.showAll", { count: problems.length })}
          </Button>
        ) : null}
        {truncated ? <p>{t("admin.imports.truncated", { count: problems.length })}</p> : null}
      </div>
    </Card>
  );
}
